import {useEffect, useCallback, useReducer} from 'react';
import Mousetrap from 'mousetrap';
import _ from 'lodash';

const initialState = {
    // Array of previous state values updated each time we push a new state
    past: [],
    // Current state value
    present: null,
    presentCopy: null,
    // Will contain "future" state values if we undo (so we can redo)
    future: [],
};

const reducer = (state, action) => {
    const {past, present, future, presentCopy} = state;
    switch (action.type) {
        case 'UNDO':
            if (past.length === 0) {
                return state;
            }
            const previous = past[past.length - 1];
            const newPast = past.slice(0, past.length - 1);
            return {
                past: newPast,
                present: previous,
                presentCopy: _.cloneDeep(previous),
                future: [present, ...future],
            };
        case 'REDO':
            if (future.length === 0) {
                return state;
            }
            const next = future[0];
            const newFuture = future.slice(1);
            return {
                past: [...past, present],
                present: next,
                presentCopy: _.cloneDeep(next),
                future: newFuture,
            };
        case 'SET':
            const {newPresent} = action;
            const clonedPresent = _.cloneDeep(newPresent);
            if (newPresent === present) {
                return state;
            }
            return {
                past: [...past, presentCopy],
                present: clonedPresent,
                presentCopy: _.cloneDeep(clonedPresent),
                future: [],
            };
        case 'SET_NO_HISTORY':
            const {newPresentNoHistory} = action;
            const clonedPresentNoHistory = _.cloneDeep(newPresentNoHistory);

            return {
                past: [...past],
                present: clonedPresentNoHistory,
                presentCopy: _.cloneDeep(clonedPresentNoHistory),
                future: [],
            };
        case 'CLEAR':
            const {initialPresent} = action;
            return {
                ...initialState,
                present: initialPresent,
                presentCopy: _.cloneDeep(initialPresent),
            };
    }
};

export function useStateWithUndo<T>(
    starting: T,
    options?: {disabled?: boolean}
): [T, (newState: T) => void, {undo: (e?: Mousetrap.ExtendedKeyboardEvent) => void}] {
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        present: starting,
        presentCopy: _.cloneDeep(starting),
    });

    const undo = useCallback(
        (e?: Mousetrap.ExtendedKeyboardEvent) => {
            if (options?.disabled) {
                return;
            }
            // check if length is over max
            if (e) {
                e.preventDefault();
            }

            dispatch({type: 'UNDO'});
        },
        [options?.disabled]
    );

    const redo = useCallback(
        (e?: Mousetrap.ExtendedKeyboardEvent) => {
            if (options?.disabled) {
                return;
            }
            // check if length is over max
            if (e) {
                e.preventDefault();
            }

            dispatch({type: 'REDO'});
        },
        [options?.disabled]
    );

    useEffect(() => {
        // setup cmd z listener and redo listener
        Mousetrap.bind('command+z', undo);
        Mousetrap.bind('command+shift+z', redo);
    }, [undo, redo]);

    const updateState = useCallback(
        (newState: T) => {
            if (options?.disabled) {
                dispatch({type: 'SET_NO_HISTORY', newPresentNoHistory: newState});
            } else {
                dispatch({type: 'SET', newPresent: newState});
            }
        },
        [options?.disabled]
    );
    return [state.present, updateState, {undo}];
}
